import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import { Subscription } from 'rxjs';

import { ConfirmDialogConfig } from '@models/dialog';
import {
  FormattedKaraokePlayer,
  IFormattedKaraokePlayer,
  IKaraokeDTO,
  IKaraokePlayer,
  KaraokeDTO,
  KaraokePlayer,
} from '@models/grade-unit/karaoke';
import { ModalService } from '@core/services/modal.service';
import { SaveCancelButtonConfig } from '@models/form/button';
import { KaraokePlayerService } from '@app/infrastructure/core/services/karaoke-player.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-karaoke-step',
  template: `
    <app-karaoke-step-presentation
      [form]="form"
      [formatAllForm]="formatAllForm"
      [formattedKaraokePlayer]="formattedKaraokePlayer"
      [transcriptWordList]="transcriptWordList"
      [block]="karaoke"
      (emitResetTranscript)="openResetDialog()"
    ></app-karaoke-step-presentation>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KaraokeStepSmartComponent implements OnInit, OnDestroy {
  @Input() public karaoke: IKaraokeDTO = new KaraokeDTO();
  @Output()
  public emitTranscriptUpdated: EventEmitter<IFormattedKaraokePlayer> = new EventEmitter<IFormattedKaraokePlayer>();
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formatAllForm: UntypedFormGroup;
  public karaokePlayer: IKaraokePlayer = new KaraokePlayer();
  public formattedKaraokePlayer: IFormattedKaraokePlayer =
    new FormattedKaraokePlayer();

  private subscriptions: Subscription[] = [];

  constructor(
    private cd: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private modalService: ModalService,
    private karaokePlayerService: KaraokePlayerService,
  ) {}

  public ngOnInit(): void {
    this.karaokePlayer = this.karaokePlayerService.mapKaraokeDTOToKaraokePlayer(
      this.karaoke,
    );
    this.formattedKaraokePlayer =
      this.karaokePlayerService.mapToFormattedPlayer(this.karaokePlayer);
    this.form = this.buildForm(this.karaokePlayer);
    this.buildFormatAllForm();
    this.updateFormattedTranscript();
    this.updateFormatAll();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public get transcriptWordList(): UntypedFormArray {
    return this.form?.get('transcriptWordList') as UntypedFormArray;
  }

  public openResetDialog(): void {
    const dialogConfig = new ConfirmDialogConfig({
      title: `Reset the transcript? Resetting will restore the original unformatted transcript.`,
      submit: new SaveCancelButtonConfig({
        save: 'Reset',
      }),
    });
    this.modalService
      .openConfirmDialog(dialogConfig)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.resetForm();
          this.cd.markForCheck();
        }
      });
  }

  private buildOriginalTranscriptForm(
    form: UntypedFormGroup,
    karaokePlayer: IKaraokePlayer,
  ): void {
    const transcriptWordList = form.get(
      'transcriptWordList',
    ) as UntypedFormArray;
    karaokePlayer?.transcriptWordList.forEach((transcriptWord) => {
      const transcriptWordFormGroup = this.fb.group({
        id: new UntypedFormControl(transcriptWord.id),
        start: new UntypedFormControl(transcriptWord.start),
        end: new UntypedFormControl(transcriptWord.end),
        content: new UntypedFormControl(transcriptWord.content),
        isBlue: new UntypedFormControl(transcriptWord.isBlue),
        isHighlighted: new UntypedFormControl(transcriptWord.isHighlighted),
        isBold: new UntypedFormControl(transcriptWord.isBold),
        isItalicized: new UntypedFormControl(transcriptWord.isItalicized),
        hasHeader: new UntypedFormControl(transcriptWord.hasHeader),
        hasNewline: new UntypedFormControl(transcriptWord.hasNewline),
      });
      transcriptWordList.push(transcriptWordFormGroup);
    });
  }

  private buildForm(karaokePlayer: IKaraokePlayer): UntypedFormGroup {
    const form = this.fb.group({
      audioUrl: new UntypedFormControl(karaokePlayer?.audioUrl),
      transcriptWordList: new UntypedFormArray([]),
    });

    const transcriptWordList = form.get(
      'transcriptWordList',
    ) as UntypedFormArray;
    if (karaokePlayer?.editedTranscriptWordList) {
      for (const editedTranscriptWord of karaokePlayer?.editedTranscriptWordList) {
        editedTranscriptWord.wordList.forEach((transcriptWord) => {
          const transcriptWordFormGroup = this.fb.group({
            id: new UntypedFormControl(transcriptWord.id),
            start: new UntypedFormControl(transcriptWord.start),
            end: new UntypedFormControl(transcriptWord.end),
            content: new UntypedFormControl(transcriptWord.content),
            isBlue: new UntypedFormControl(transcriptWord.isBlue),
            isHighlighted: new UntypedFormControl(transcriptWord.isHighlighted),
            isBold: new UntypedFormControl(transcriptWord.isBold),
            isItalicized: new UntypedFormControl(transcriptWord.isItalicized),
            hasHeader: new UntypedFormControl(transcriptWord.hasHeader),
            hasNewline: new UntypedFormControl(transcriptWord.hasNewline),
          });
          transcriptWordList.push(transcriptWordFormGroup);
        });
      }
    } else {
      this.buildOriginalTranscriptForm(form, karaokePlayer);
    }

    return form;
  }

  private buildFormatAllForm(): void {
    const headerSelected =
      this.karaokePlayer?.editedTranscriptWordList?.every(
        (editedTranscription) =>
          editedTranscription.wordList.every(
            (transcript) => transcript.hasHeader,
          ),
      ) || null;
    const captionSelected =
      this.karaokePlayer?.editedTranscriptWordList?.every(
        (editedTranscription) =>
          editedTranscription.wordList.every(
            (transcript) => transcript.isItalicized,
          ),
      ) || null;
    const promptSelected =
      this.karaokePlayer?.editedTranscriptWordList?.every(
        (editedTranscription) =>
          editedTranscription.wordList.every((transcript) => transcript.isBlue),
      ) || null;
    this.formatAllForm = this.fb.group({
      caption: [captionSelected],
      header: [headerSelected],
      prompt: [promptSelected],
    });
  }

  private updateFormatAll(): void {
    const formatAllSubscription = this.formatAllForm.valueChanges.subscribe(
      (value: { header: boolean; caption: boolean; prompt: boolean }) => {
        this.karaokePlayer.transcriptWordList.forEach((transcript) => {
          transcript.hasHeader =
            value.header != null ? value.header : transcript.hasHeader;
          transcript.isItalicized =
            value.caption != null ? value.caption : transcript.isItalicized;
          transcript.isBlue =
            value.prompt != null ? value.prompt : transcript.isBlue;
        });
        this.form.setValue({
          audioUrl: this.karaokePlayer.audioUrl,
          transcriptWordList: this.karaokePlayer.transcriptWordList,
        });
      },
    );
    this.subscriptions.push(formatAllSubscription);
  }

  private updateFormattedTranscript(): void {
    const formValueSubscription = this.form.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((updatedFormValue) => {
        this.karaokePlayer = updatedFormValue;
        this.formattedKaraokePlayer =
          this.karaokePlayerService.mapToFormattedPlayer(this.karaokePlayer);
        this.emitTranscriptUpdated.emit(this.formattedKaraokePlayer);
      });

    this.subscriptions.push(formValueSubscription);
  }
  private resetForm(): void {
    this.karaoke.editedTranscription = null;

    this.karaokePlayer = this.karaokePlayerService.mapKaraokeDTOToKaraokePlayer(
      this.karaoke,
    );

    const form = this.fb.group({
      audioUrl: new UntypedFormControl(this.karaokePlayer?.audioUrl),
      transcriptWordList: new UntypedFormArray([]),
    });

    this.buildOriginalTranscriptForm(form, this.karaokePlayer);

    this.form = form;

    this.updateFormattedTranscript();

    this.formattedKaraokePlayer =
      this.karaokePlayerService.mapToFormattedPlayer(this.karaokePlayer);
    this.transcriptWordList.setValue(this.karaokePlayer.transcriptWordList);
    this.buildFormatAllForm();
    this.updateFormatAll();
  }
}
